import { useState } from "react";
import { NavLink, Navigate, Outlet, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, Tags, Image, Truck, ClipboardList, Store, LogOut, Menu, Star, Settings2, CreditCard, BellRing, Users, FileText, Search, Paintbrush, Megaphone,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/produtos", label: "Produtos", icon: Package },
  { to: "/admin/categorias", label: "Categorias", icon: Tags },
  { to: "/admin/banners", label: "Banners", icon: Image },
  { to: "/admin/home", label: "Editor da Home", icon: Paintbrush },
  { to: "/admin/marketing", label: "Marketing", icon: Megaphone },
  { to: "/admin/entregas", label: "Entregas", icon: Truck },
  { to: "/admin/pagamentos", label: "Pagamentos", icon: CreditCard },
  { to: "/admin/pedidos", label: "Pedidos", icon: ClipboardList },
  { to: "/admin/notificacoes", label: "Notificações", icon: BellRing },
  { to: "/admin/avaliacoes", label: "Avaliações", icon: Star },
  { to: "/admin/clientes", label: "Clientes", icon: Users },
  { to: "/admin/paginas", label: "Páginas", icon: FileText },
  { to: "/admin/seo", label: "SEO / Google", icon: Search },
  { to: "/admin/configuracoes", label: "Site e Textos", icon: Settings2 },
];

const NavItems = ({ onNavigate }) => (
  <nav className="space-y-1">
    {NAV.map(({ to, label, icon: Icon, end }) => (
      <NavLink
        key={to}
        to={to}
        end={end}
        onClick={onNavigate}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            isActive ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-100"
          }`
        }
        data-testid={`admin-nav-${label.toLowerCase()}`}
      >
        <Icon className="w-4 h-4" /> {label}
      </NavLink>
    ))}
  </nav>
);

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand border-t-transparent" data-testid="admin-loading" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-slate-100 grid md:grid-cols-[240px_1fr]" data-testid="admin-layout">
      {/* sidebar desktop */}
      <aside className="hidden md:flex flex-col bg-white border-r border-slate-200 p-4 sticky top-0 h-screen">
        <Link to="/admin" className="flex items-center gap-2 px-2 py-3">
          <img src="/assets/logo.png" alt="HG" className="h-9 w-9" />
          <span className="font-heading font-bold text-gray-900">Painel HG</span>
        </Link>
        <div className="mt-4 flex-1">
          <NavItems />
        </div>
        <div className="space-y-1 pt-4 border-t border-slate-100">
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100"
            data-testid="admin-nav-store"
          >
            <Store className="w-4 h-4" /> Ver Loja
          </Link>
          <button
            onClick={doLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4" /> Sair
          </button>
        </div>
      </aside>

      <div className="min-w-0">
        {/* topbar mobile */}
        <div className="md:hidden sticky top-0 z-40 bg-white border-b border-slate-200 flex items-center gap-2 px-4 py-3">
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="admin-mobile-menu">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72">
              <div className="flex items-center gap-2 mb-6">
                <img src="/assets/logo.png" alt="HG" className="h-9 w-9" />
                <span className="font-heading font-bold">Painel HG</span>
              </div>
              <NavItems onNavigate={() => setMenuOpen(false)} />
              <button
                onClick={doLogout}
                className="mt-6 w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50"
                data-testid="admin-logout-mobile"
              >
                <LogOut className="w-4 h-4" /> Sair
              </button>
            </SheetContent>
          </Sheet>
          <span className="font-heading font-bold text-gray-900">Painel HG Móveis</span>
          <span className="ml-auto text-xs text-slate-500">{user.email}</span>
        </div>

        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
